import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import SuccessToast from '../../../components/ui/SuccessToast';

const AddVehicleModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    licensePlate: '',
    type: '',
    fuelType: '',
    weight: '',
    axles: '2',
    isEV: false,
    isHybrid: false
  });

  const [errors, setErrors] = useState({});
  const [isClosing, setIsClosing] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);
  const [showToast, setShowToast] = useState(false);

  const vehicleTypeOptions = [
    { value: 'personbil', label: 'Personbil', description: 'Vanlig personbil' },
    { value: 'varebil', label: 'Varebil', description: 'Lett varebil under 3500 kg' },
    { value: 'lastebil', label: 'Lastebil', description: 'Tung lastebil over 3500 kg' },
    { value: 'motorsykkel', label: 'Motorsykkel', description: 'To-hjuls kjøretøy' }
  ];

  const fuelTypeOptions = [
    { value: 'bensin', label: 'Bensin' },
    { value: 'diesel', label: 'Diesel' },
    { value: 'elektrisk', label: 'Elektrisk' },
    { value: 'hybrid', label: 'Hybrid' },
    { value: 'hydrogen', label: 'Hydrogen' }
  ];

  const axleOptions = [
    { value: '2', label: '2 aksler' },
    { value: '3', label: '3 aksler' },
    { value: '4', label: '4+ aksler' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    if (field === 'fuelType') {
      if (value === 'elektrisk') {
        setFormData(prev => ({ ...prev, isEV: true, isHybrid: false }));
      } else if (value === 'hybrid') {
        setFormData(prev => ({ ...prev, isEV: false, isHybrid: true }));
      } else {
        setFormData(prev => ({ ...prev, isEV: false, isHybrid: false }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Vennligst angi kjøretøynavn';
    }

    if (!formData?.licensePlate?.trim()) {
      newErrors.licensePlate = 'Vennligst angi registreringsnummer';
    } else if (!/^[A-Z]{2}\d{5}$/?.test(formData?.licensePlate?.replace(/\s/g, ''))) {
      newErrors.licensePlate = 'Ugyldig format (f.eks. AB12345)';
    }

    if (!formData?.type) {
      newErrors.type = 'Vennligst velg kjøretøytype';
    }

    if (!formData?.fuelType) {
      newErrors.fuelType = 'Vennligst velg drivstofftype';
    }

    if (!formData?.weight) {
      newErrors.weight = 'Vennligst angi vekt';
    } else if (isNaN(formData?.weight) || formData?.weight <= 0) {
      newErrors.weight = 'Vekt må være et positivt tall';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSave({
        ...formData,
        id: `vehicle_${Date.now()}`,
        licensePlate: formData?.licensePlate?.toUpperCase()?.replace(/\s/g, '')
      });
      setShowToast(true);
      setTimeout(() => handleClose(), 500);
    } else {
      setShakeKey(k => k + 1);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setFormData({
        name: '',
        licensePlate: '',
        type: '',
        fuelType: '',
        weight: '',
        axles: '2',
        isEV: false,
        isHybrid: false
      });
      setErrors({});
      onClose();
    }, 300);
  };

  if (!isOpen && !isClosing) return null;

  return (
    <div className={`fixed inset-0 z-modal flex items-center justify-center p-16 ${isClosing ? 'modal-backdrop-exit' : 'modal-backdrop-enter'}`}>
      <div
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div
        key={`modal-${shakeKey}`}
        className={`relative w-full max-w-2xl max-h-[90vh] bg-card border border-border rounded-lg glow-lg overflow-hidden ${isClosing ? 'modal-spring-exit' : 'modal-spring-enter'} ${shakeKey > 0 ? 'form-shake' : ''}`}
      >
        <div className="flex items-center justify-between p-24 border-b border-border">
          <div className="flex items-center space-x-12">
            <div className="w-48 h-48 flex items-center justify-center bg-primary/10 rounded-lg">
              <Icon name="Plus" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-h4 font-heading font-semibold text-foreground">
                Legg til nytt kjøretøy
              </h2>
              <p className="text-caption text-muted-foreground mt-4">
                Registrer kjøretøyinformasjon for nøyaktige bompengeberegninger
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-8 rounded-md hover:bg-muted/50 transition-smooth"
            aria-label="Lukk modal"
          >
            <Icon name="X" size={24} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-24 overflow-y-auto max-h-[calc(90vh-180px)] scrollbar-custom">
          <div className="space-y-24">
            <div className="space-y-16">
              <h3 className="text-base font-medium text-foreground">Grunnleggende informasjon</h3>
              
              <Input
                label="Kjøretøynavn"
                type="text"
                placeholder="F.eks. Min Tesla Model 3"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                error={errors?.name}
                required
                description="Et beskrivende navn for å identifisere kjøretøyet"
              />

              <Input
                label="Registreringsnummer"
                type="text"
                placeholder="AB12345"
                value={formData?.licensePlate}
                onChange={(e) => handleInputChange('licensePlate', e?.target?.value)}
                error={errors?.licensePlate}
                required
                description="Norsk registreringsnummer (2 bokstaver + 5 tall)"
              />
            </div>

            <div className="space-y-16">
              <h3 className="text-base font-medium text-foreground">Kjøretøyspesifikasjoner</h3>

              <Select
                label="Kjøretøytype"
                options={vehicleTypeOptions}
                value={formData?.type}
                onChange={(value) => handleInputChange('type', value)}
                error={errors?.type}
                required
                placeholder="Velg type..."
              />

              <Select
                label="Drivstofftype"
                options={fuelTypeOptions}
                value={formData?.fuelType}
                onChange={(value) => handleInputChange('fuelType', value)}
                error={errors?.fuelType}
                required
                placeholder="Velg drivstoff..."
                description="Påvirker rabattberegninger"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                <Input
                  label="Totalvekt (kg)"
                  type="number"
                  placeholder="1500"
                  value={formData?.weight}
                  onChange={(e) => handleInputChange('weight', e?.target?.value)}
                  error={errors?.weight}
                  required
                  min="0"
                />

                <Select
                  label="Antall aksler"
                  options={axleOptions}
                  value={formData?.axles}
                  onChange={(value) => handleInputChange('axles', value)}
                  description="Påvirker bompengetakster"
                />
              </div>
            </div>

            {(formData?.isEV || formData?.isHybrid) && (
              <div className="p-16 bg-success/10 border border-success/20 rounded-lg">
                <div className="flex items-start space-x-12">
                  <Icon name="Zap" size={20} color="var(--color-success)" className="flex-shrink-0 mt-2" />
                  <div>
                    <h4 className="text-base font-medium text-success mb-4">
                      Rabatt kvalifisert
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {formData?.isEV && 'Dette kjøretøyet kvalifiserer for 50% EL-rabatt på de fleste bomstasjoner.'}
                      {formData?.isHybrid && 'Dette kjøretøyet kvalifiserer for 20% hybrid-rabatt på enkelte bomstasjoner.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </form>

        <div className="flex items-center justify-end space-x-12 p-24 border-t border-border bg-muted/30">
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={handleClose}
          >
            Avbryt
          </Button>
          <Button
            type="submit"
            variant="default"
            size="lg"
            iconName="Check"
            iconPosition="left"
            onClick={handleSubmit}
          >
            Lagre kjøretøy
          </Button>
        </div>
      </div>
      <SuccessToast
        visible={showToast}
        message="Kjøretøy lagret!"
        subtext="Kjøretøyet er lagt til i din profil."
        onDismiss={() => setShowToast(false)}
      />
    </div>
  );
};

export default AddVehicleModal;